import Constants from "expo-constants";
import * as Device from "expo-device";
import * as Notifications from "expo-notifications";
import { Platform } from "react-native";

import i18n, { isAppLanguage } from "@/i18n/i18n";
import { getParentDeviceInfo } from "@/lib/deviceInfo";
import { ensureFeathersReady, getFeathersClient } from "@/lib/feathers/client";
import { log } from "@/logger";

import { getDeviceFcmToken } from "./getDeviceFcmToken";
import { getOrCreateInstallationId } from "./installationId";
import { getPushNotificationsEnabled } from "./pushPreference";
import { requestSystemNotificationPermission } from "./systemPermission";

type PushDeviceRecord = { id: number };

function firstPushDevice(result: unknown): PushDeviceRecord | null {
    if (Array.isArray(result)) {
        return (result[0] as PushDeviceRecord | undefined) ?? null;
    }

    return null;
}

async function findCurrentPushDevice(): Promise<PushDeviceRecord | null> {
    await ensureFeathersReady();
    const installationId = await getOrCreateInstallationId();
    const result = await getFeathersClient()
        .service("push-device")
        .find({
            query: { installation_id: installationId },
        });
    return firstPushDevice(result);
}

function resolveLocale(): "cs" | "en" {
    return isAppLanguage(i18n.language) && i18n.language === "cs" ? "cs" : "en";
}

/** Must match `android.notification.channelId` values from s4p-server sendPush. */
const ANDROID_CHANNELS = [
    {
        id: "default",
        name: "Default",
        importance: Notifications.AndroidImportance.DEFAULT,
    },
    {
        id: "absence_reminder",
        name: "Absence reminders",
        importance: Notifications.AndroidImportance.HIGH,
    },
] as const;

async function ensureAndroidChannels(): Promise<void> {
    if (Platform.OS !== "android") {
        return;
    }

    for (const channel of ANDROID_CHANNELS) {
        await Notifications.setNotificationChannelAsync(channel.id, {
            name: channel.name,
            importance: channel.importance,
        });
    }
}

/**
 * Requests permission (if needed), obtains an FCM registration token,
 * and upserts the push-device record on s4p-server.
 */
export async function registerPushDevice(): Promise<void> {
    if (Platform.OS === "web") {
        log.debug("Not registering push device on web.");
        return;
    }

    if (!Constants.expoConfig?.extra?.pushEnabled) {
        log.debug("Push is disabled in app config; skipping registration.");
        return;
    }

    const enabled = await getPushNotificationsEnabled();
    if (!enabled) {
        return;
    }

    try {
        await ensureFeathersReady();
        await ensureAndroidChannels();

        const permission = await requestSystemNotificationPermission();
        if (permission !== "granted") {
            log.info("Push notification permission not granted.");
            return;
        }

        const token = await getDeviceFcmToken();
        if (!token) {
            log.warn("No FCM push token available.");
            return;
        }

        const installationId = await getOrCreateInstallationId();
        const deviceInfo = getParentDeviceInfo();

        await getFeathersClient()
            .service("push-device")
            .create({
                token,
                platform: deviceInfo.type,
                installation_id: installationId,
                locale: resolveLocale(),
                device_name: deviceInfo.device_name ?? null,
                os_version: deviceInfo.os_version ?? null,
                app_version: deviceInfo.app_version ?? null,
                enabled: true,
            });

        log.debug("Push device registered", {
            platform: deviceInfo.type,
            installationId,
            projectId: Constants.expoConfig?.slug,
        });
    } catch (error) {
        log.warn("Failed to register push device", error);
    }
}

/** Patches locale for the current installation after language change. */
export async function updatePushDeviceLocale(
    locale: "cs" | "en",
): Promise<void> {
    if (Platform.OS === "web") {
        return;
    }

    try {
        const device = await findCurrentPushDevice();
        if (!device) {
            await registerPushDevice();
            return;
        }

        await getFeathersClient()
            .service("push-device")
            .patch(device.id, { locale });
    } catch (error) {
        log.warn("Failed to update push device locale", error);
    }
}

/** Disables or removes the current installation's push registration. */
export async function unregisterPushDevice(): Promise<void> {
    if (Platform.OS === "web") {
        return;
    }

    try {
        await ensureFeathersReady();
        const installationId = await getOrCreateInstallationId();
        await getFeathersClient()
            .service("push-device")
            .remove(null, { query: { installation_id: installationId } });
    } catch (error) {
        log.warn("Failed to unregister push device", error);
    }
}

/** Soft-disables push for the current installation (keeps the row). */
export async function setPushDeviceEnabled(enabled: boolean): Promise<void> {
    if (Platform.OS === "web") {
        return;
    }

    try {
        const device = await findCurrentPushDevice();
        if (!device) {
            if (enabled) {
                await registerPushDevice();
            } else {
                log.info("No push device to disable for this installation.");
            }
            return;
        }

        await getFeathersClient()
            .service("push-device")
            .patch(device.id, { enabled });
        log.debug("Push device enabled flag updated", {
            enabled,
            id: device.id,
        });
    } catch (error) {
        log.warn("Failed to update push device enabled flag", error);
    }
}
